import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';

import { LaunchpoolStatus } from '@flipster/apis/v1/launchpool/types';
import SegmentClick from '@flipster/features/src/misc/segment/segment-click';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useV1Currencies } from '@flipster/features/src/shared/currency/use-v1-currencies';
import { launchpoolItemBunja } from '@flipster/features/src/shared/launchpool/state';
import { useDepositModal } from '@flipster/features/src/shared/wallet-modal/deposit';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { CountdownDate } from '@flipster/react/design-utils/countdown-date';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import ShieldCheckIcon from '@flipster/react/icon/generated/ShieldCheckIcon';
import SparklesIcon from '@flipster/react/icon/generated/SparklesIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { useLocalStorage } from '@flipster/react/misc/state/use-local-storage';
import { css, cx } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { alert } from '@flipster/ui/Alert';
import { DateFormatter, isSameUTCYear } from '@flipster/utils/datetime';
import Num, { numberFormatter } from '@flipster/utils/num';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import {
  currencyCssRaw,
  dividerCssRaw,
  dlCssRaw,
  layoutCssRaw,
} from '@web/app/[locale]/(web)/launchpool/_lists/_Item/shared-css';

import StakingModal from '../../_modals/staking-modal';
import { getVIPEstimatedApr } from '../utils';
import StakingStatusBadge from './LaunchpoolStatusBadge';

const LaunchpoolInfo = () => {
  const t = useTranslations();
  const router = useRouter();
  const overlay = useOverlay();

  const { isLoggedIn, user } = useAuth();
  const { isMobile } = useBreakpoint();

  const { launchpoolItemAtom } = useBunja(launchpoolItemBunja);
  const launchpool = useAtomValue(launchpoolItemAtom);
  const { currenciesKeyByCurrency } = useV1Currencies();
  const openDepositModal = useDepositModal();
  const [isSkipHedgingNotice] = useLocalStorage({
    key: `flipster.configs.v1.${user?.userId}.skipHedgingNoticeConfirm`,
    defaultValue: false,
  });

  const {
    id,
    stakingCurrency,
    rewardCurrency,
    status,
    totalReward,
    startAt,
    endAt,
    estimatedApr,
    firstDepositorBannerStatus,
    totalStaked,
    stakedAmount,
    vipMultiplier,
    multipliers,
    showHedge,
    minVipTierForMultiplier,
  } = launchpool;

  const VIPEstimatedApr = getVIPEstimatedApr(estimatedApr, vipMultiplier);
  const isSameYear = isSameUTCYear(startAt, endAt);
  const isClosed = status === LaunchpoolStatus.Closed;
  const isUpcoming = status === LaunchpoolStatus.Upcoming;
  const isOngoing = status === LaunchpoolStatus.Ongoing;
  const rewardCurrencyPrecision = currenciesKeyByCurrency?.[rewardCurrency]?.blockchainInfo[0]?.withdrawalPrecision;
  const stakingCurrencyPrecision = currenciesKeyByCurrency?.[stakingCurrency]?.blockchainInfo[0]?.withdrawalPrecision;
  const firstDepositMultiplier = multipliers.find((tvm) => tvm.multiplierType === 'MULTIPLIER_TYPE_FIRST_DEPOSIT');

  const firstDepositorApr =
    estimatedApr &&
    firstDepositMultiplier?.multiplier &&
    Num(estimatedApr).multipliedBy(firstDepositMultiplier.multiplier);
  const isShowFirstDepositBanner = firstDepositorBannerStatus !== 'FIRST_DEPOSITOR_BANNER_STATUS_NO_BANNER';
  const isNotFirstDepositedStatus = firstDepositorBannerStatus === 'FIRST_DEPOSITOR_BANNER_STATUS_NOT_DEPOSITED_BANNER';
  const isShowHedgeBanner = showHedge && !isMobile && !isClosed && Num(stakedAmount).isGreaterThan(0);

  const onClickAprInfoButton = () => {
    alert.show({
      title: <FormattedMessage id='Autogen_common.r251126.s_Estimated_APR' />,
      message: (
        <div>
          <FormattedMessage id='Autogen_common.r251126.s_The_rate_on_the_page_is_for_reference_only' />
          <br />
          <br />
          <FormattedMessage id='Autogen_common.r251126.s_The_Annual_Percentage_Rate_APR_depends_on_colon' />
          <ul>
            <li>
              <FormattedMessage id='Autogen_common.r251126.s_Daily_distributed_rewards_sum_Multiplier_staked_amounts_across_users_365' />
            </li>
            <li>
              <FormattedMessage id='Autogen_common.r251126.s_Historical_data_calculated_at_the_most_recent_00_00_UTC' />
            </li>
          </ul>
          <br />
          <FormattedMessage id='Autogen_common.r251126.s_Higher_total_staked_tokens_may_reduce_the_projected_APR' />
        </div>
      ),
      confirmText: <FormattedMessage id='Common.r250401.Ok' />,
    });
  };

  const onClickDepositBanner = () => {
    if (firstDepositorBannerStatus === 'FIRST_DEPOSITOR_BANNER_STATUS_FIRST_DEPOSITED_BANNER') return;
    if (!isLoggedIn) {
      router.push('/login');
      return;
    }
    openDepositModal();
  };

  const onClickHedgingBanner = () => {
    overlay.open(({ isOpen, onClose, unmount }) => (
      <StakingModal
        launchpoolId={id}
        isOpen={isOpen}
        onClose={onClose}
        step={isSkipHedgingNotice ? 'hedging' : 'hedgingNotice'}
        onExit={unmount}
      />
    ));
  };

  return (
    <div className={cx(css(layoutCssRaw, { flex: 1 }), isClosed && 'closed')}>
      <dl className={css(dlCssRaw)}>
        <div>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Total_rewards' />
          </dt>
          <dd>
            <Formatted.Number
              value={totalReward}
              suffix={<span className={css(currencyCssRaw)}>{rewardCurrency}</span>}
              precision={rewardCurrencyPrecision}
              zeroString='-'
            />
          </dd>
        </div>
        <div>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Staking_period' />
          </dt>
          <dd>
            <Datetime value={startAt} format={DateFormatter.ShortYearDateTime} isUTC />
            <br />
            {'- '}
            <Datetime
              value={endAt}
              format={isSameYear ? DateFormatter.MonthDateTime : DateFormatter.ShortYearDateTime}
              isUTC
              withUTCText
            />
          </dd>
        </div>
        <div>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Status' />
          </dt>
          <dd>
            <StakingStatusBadge status={status} />
            {(isUpcoming || isOngoing) && (
              <CountdownDate
                targetDate={isUpcoming ? startAt : endAt}
                format='${dd}D ${hh}H ${mm}M ${ss}S left'
                css={{ color: 'content.default.level-2', mt: 4 }}
              />
            )}
          </dd>
        </div>
        <div className={css(dividerCssRaw)} />
        <div>
          <dt className={hstack()}>
            <span>
              <FormattedMessage id='Autogen_common.r251126.s_Estimated_APR' />
            </span>
            <button type='button' onClick={onClickAprInfoButton} className={css({ buttonShape: 'inline-icon', ml: 4 })}>
              <InformationCircleIcon size={16} color={token('colors.content.default.level-1')} />
            </button>
          </dt>
          <dd>
            <Formatted.Percent
              value={estimatedApr}
              css={{
                textStyle: 'Title/XS',
                color: isClosed ? 'content.default.level-2' : 'content.state.positive',
              }}
            />
            <div>
              {t('Autogen_common.r251126.s_VIP_minVipTierForMultiplier_get', {
                minVipTierForMultiplier: minVipTierForMultiplier,
              })}
              <Formatted.Percent value={VIPEstimatedApr} />
            </div>
          </dd>
        </div>
        {isShowFirstDepositBanner && (
          <button
            type='button'
            onClick={onClickDepositBanner}
            className={hstack({
              w: '100%',
              justify: 'space-between',
              gap: 8,
              py: 8,
              px: 12,
              my: 8,
              bgColor: 'background.default.level-3',
              rounded: 4,
            })}
          >
            <SparklesIcon size={16} className={css({ color: 'content.accent.promotion' })} />
            <div className={css({ flex: 1, textAlign: 'left' })}>
              <p className={css({ textStyle: 'Text/S/Strong', color: 'content.default.level-5' })}>
                {isNotFirstDepositedStatus && t('Autogen_common.r251126.s_Deposit_and_get')}
                {!isNotFirstDepositedStatus && t('Autogen_common.r251126.s_Stake_to_activate')}
                {numberFormatter(Number(firstDepositorApr), { withComma: true, precision: 2 })}% APR (
                {firstDepositMultiplier?.multiplier}x)
              </p>
              <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>
                {isNotFirstDepositedStatus && t('Autogen_common.r251126.s_For_new_users_only_on_your_first_deposit')}
                {!isNotFirstDepositedStatus &&
                  t('Autogen_common.r251126.s_Will_be_automatically_applied_when_you_stake')}
              </p>
            </div>
            {isNotFirstDepositedStatus && (
              <ChevronRightIcon size={16} color={token('colors.content.default.level-2')} />
            )}
          </button>
        )}
        <div className={css(dividerCssRaw)} />
        <div>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Total_staked' />
          </dt>
          <dd>
            <Formatted.Number
              value={totalStaked}
              suffix={<span className={css(currencyCssRaw)}>{stakingCurrency}</span>}
              precision={stakingCurrencyPrecision}
              zeroString='-'
            />
          </dd>
        </div>
        <div>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_You_staked' />
          </dt>
          <dd>
            <Formatted.Number
              value={stakedAmount}
              suffix={<span className={css(currencyCssRaw)}>{stakingCurrency}</span>}
              precision={stakingCurrencyPrecision}
            />
          </dd>
        </div>
        {isShowHedgeBanner && (
          <SegmentClick
            event={StakeEvent.StakeHedgeClick}
            data={{ currency: stakingCurrency, event_location: 'earn_launchpool_tab' }}
          >
            <button
              type='button'
              onClick={onClickHedgingBanner}
              className={hstack({
                justifyContent: 'space-between',
                px: 12,
                py: 8,
                mt: 8,
                w: '100%',
                rounded: 4,
                bgColor: 'background.default.level-3',
              })}
            >
              <p
                className={css({
                  display: 'inline-flex',
                  textStyle: 'Text/S/Strong',
                  color: 'content.default.level-3',
                  gap: 8,
                })}
              >
                <ShieldCheckIcon size={16} className={css({ color: 'content.default.level-5' })} />
                <FormattedMessage id='Autogen_common.r251126.s_Hedge_to_protect_your_stake' />
              </p>
              <ChevronRightIcon size={16} className={css({ color: 'content.default.level-2' })} />
            </button>
          </SegmentClick>
        )}
      </dl>
    </div>
  );
};

export default LaunchpoolInfo;
